// src/config/features.js
// 🚦 Feature Flags - Controle de funcionalidades
// Versão: 2.0 | Preços sincronizados com src/config/plans.js (Single Source of Truth)
// Planos: Basic (R$47 lançamento) · Professional (R$97 lançamento) · Enterprise (R$247 lançamento)

/**
 * Este arquivo controla quais funcionalidades estão ativas no sistema.
 * Por segurança, todas as features começam DESATIVADAS (false).
 * 
 * Para ativar um recurso, mude enabled para true.
 * Para testar localmente, use environment = 'sandbox'
 * 
 * ⚠️ PREÇOS: A fonte real é src/config/plans.js.
 *            Este arquivo é apenas um resumo para feature flags.
 */

export const features = {
  // 💰 Sistema de Pagamentos
  payments: {
    enabled: true,
    gateway: 'mercadopago',       // 'mercadopago' | 'stripe'
    environment: 'sandbox',       // 'sandbox' | 'production'
    
    // Planos disponíveis (preços de LANÇAMENTO — fonte real: src/config/plans.js)
    plans: {
      basic: { 
        id: 'basic',
        name: 'Básico', 
        price: 47,                // R$ 47,00 (lançamento)
        priceRegular: 97,         // R$ 97,00 (regular)
        description: 'Para começar',
        features: ['10 imóveis', '50 clientes', 'Calendário de visitas', 'Relatórios básicos', 'Suporte por email']
      },
      professional: { 
        id: 'professional',
        name: 'Profissional', 
        price: 97,                // R$ 97,00 (lançamento)
        priceRegular: 197,        // R$ 197,00 (regular)
        description: 'O mais popular',
        features: ['50 imóveis', '200 clientes', '5 usuários', 'Relatórios avançados', 'API de integração', 'Suporte prioritário', 'Exportação Excel/PDF']
      },
      enterprise: { 
        id: 'enterprise',
        name: 'Enterprise', 
        price: 247,               // R$ 247,00 (lançamento)
        priceRegular: 497,        // R$ 497,00 (regular)
        description: 'Solução corporativa',
        features: ['Imóveis ilimitados', 'Clientes ilimitados', 'Usuários ilimitados', 'White Label completo', 'API de integração', 'Suporte dedicado', 'Domínio personalizado', 'Onboarding dedicado']
      }
    },
    
    // Configurações de trial
    trial: {
      enabled: true,
      days: 7
    }
  },

  // 🔐 Recuperação de senha
  passwordReset: {
    enabled: true,
    method: 'emailjs'
  },

  // 📊 Analytics
  analytics: {
    enabled: false,
    provider: 'google'
  },

  // 🤖 IA Features
  ai: {
    enabled: true,
    recommendations: true,
    insights: true
  },

  // 🎨 White Label
  whiteLabel: {
    enabled: true,
    customDomain: false
  }
};

/**
 * Helper para verificar se uma feature está ativa
 * @param {string} featurePath - Caminho da feature (ex: 'payments.enabled')
 * @returns {boolean}
 */
export const isFeatureEnabled = (featurePath) => {
  try {
    const parts = featurePath.split('.');
    let current = features;
    
    for (const part of parts) {
      if (!current || !(part in current)) return false;
      current = current[part];
    }
    
    return !!current;
  } catch (error) {
    console.error('❌ Erro ao verificar feature:', error);
    return false;
  }
};

/**
 * Obtém configuração de um plano específico
 * @param {string} planId - ID do plano ('basic', 'professional', 'enterprise')
 * @returns {Object} Configuração do plano
 */
export const getPlanConfig = (planId) => {
  return features.payments?.plans?.[planId] || features.payments?.plans?.basic;
};

/**
 * Lista todos os planos disponíveis
 * @returns {Array} Array de planos
 */
export const getAllPlans = () => {
  return Object.values(features.payments?.plans || {});
};

export default features;